import { useState } from 'react';
import axiosInstance from '../axiosInstance';
import { endpoints } from '../endPoints';
import toast from 'react-hot-toast';

const useSessionTimeRangeService = () => {
    const [sessionTimeRanges, setSessionTimeRanges] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const fetchSessionTimeRanges = async (page, size, status = null) => {
        try {
            setIsLoading(true);
            const url = status
                ? `${endpoints.sessionTimeRanges.getAll}?page=${page}&size=${size}&status=${status}`
                : `${endpoints.sessionTimeRanges.getAll}?page=${page}&size=${size}`;

            const response = await axiosInstance.get(url);
            const { data } = response.data;

            setTotalRecords(data.records || 0);
            setSessionTimeRanges(Array.isArray(data.data) ? data.data.map((item) => ({
                id: item.id,
                sessionTime: item.sessionTime,
                timeDescription: item.timeDescription,
                status: item.status,
                createdAt: item.createdAt,
            })) : []);
        } catch (error) {
            toast.error('Error fetching session time ranges.');
            console.error('Fetch SessionTimeRanges Error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const createSessionTimeRange = async (sessionTimeRangeDto, onSuccess) => {
        try {
            const response = await axiosInstance.post(endpoints.sessionTimeRanges.create, sessionTimeRangeDto);
            toast.success(response.data.message || 'Session time range created successfully');
            if (onSuccess) {
                onSuccess();
            }
            return response.data;
        } catch (error) {
            console.error('Error creating session time range:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Error creating session time range';
            toast.error(errorMessage);
            throw error; // Re-throw so the form can handle it
        }
    };

    const getSessionTimeRangeById = async (id) => {
        try {
            const response = await axiosInstance.get(`${endpoints.sessionTimeRanges.get}${id}`);
            return response.data.data;
        } catch (error) {
            toast.error('Error fetching session time range details');
            console.error(error.response?.data?.message);
            throw error;
        }
    };

    const updateSessionTimeRange = async (payload) => {
        try {
            const response = await axiosInstance.put(endpoints.sessionTimeRanges.update, payload);
            toast.success(response.data.message || 'Session time range updated successfully');
            return response.data;
        } catch (error) {
            toast.error('Error updating session time range');
            console.error(error.response?.data?.message);
            throw error;
        }
    };

    const updateSessionTimeRangeStatus = async (id, currentStatus, onSuccess) => {
        const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        const apiUrl = `${endpoints.sessionTimeRanges.status}?id=${id}&status=${newStatus}`;

        try {
            const response = await axiosInstance.post(apiUrl);
            toast.success(response.data.message || `Session time range status changed to ${newStatus}`);
            onSuccess?.();
        } catch (error) {
            toast.error('Error updating session time range status.');
            console.error(error.response?.data?.message);
        }
    };

    const getAllActiveSessionTimeRanges = async () => {
        try {
            const response = await axiosInstance.get(endpoints.sessionTimeRanges.getAllActive);
            console.log('getAllActiveSessionTimeRanges - Full response:', response);
            console.log('getAllActiveSessionTimeRanges - response.data:', response.data);
            console.log('getAllActiveSessionTimeRanges - response.data.data:', response.data.data);
            
            const data = response.data.data;
            
            // Ensure we return an array
            if (Array.isArray(data)) {
                console.log(`Returning ${data.length} session times as array`);
                return data;
            } else if (data && typeof data === 'object') {
                // If it's wrapped in another object, try to extract
                const extracted = data.list || data.data || [];
                console.log('Extracted data from object:', extracted);
                return Array.isArray(extracted) ? extracted : [];
            }
            
            console.warn('No valid array found in response, returning empty array');
            return [];
        } catch (error) {
            console.error('getAllActiveSessionTimeRanges - Error:', error);
            console.error('getAllActiveSessionTimeRanges - Error response:', error.response);
            toast.error('Error fetching active session time ranges');
            console.error(error.response?.data?.message);
            throw error;
        }
    };

    const linkSessionTimeWithSubjectTeacher = async (timetableId, csrtId, day, combination) => {
        try {
            // Build query string - csrtId can be null for break times
            let url = `${endpoints.sessionTimeRanges.link}?timetableId=${timetableId}&day=${day}`;
            if (csrtId != null && csrtId !== '') {
                url += `&csrtId=${csrtId}`;
            }
            // Add combination parameter if provided (required when csrtId is provided)
            if (combination != null && combination !== '') {
                url += `&combination=${encodeURIComponent(combination)}`;
            }
            
            const response = await axiosInstance.post(url);
            toast.success(response.data.message || 'Session time linked with subject-teacher successfully');
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Error linking session time with subject-teacher';
            toast.error(errorMessage);
            throw error;
        }
    };

    const unlinkSessionTimeWithSubjectTeacher = async (timetableId, csrtId, day, combination) => {
        try {
            // Build query string - csrtId can be null for break times
            let url = `${endpoints.sessionTimeRanges.unlink}?timetableId=${timetableId}&day=${day}`;
            if (csrtId != null && csrtId !== '') {
                url += `&csrtId=${csrtId}`;
            }
            // Add combination parameter if provided (required when csrtId is provided)
            if (combination != null && combination !== '') {
                url += `&combination=${encodeURIComponent(combination)}`;
            }
            
            const response = await axiosInstance.post(url);
            toast.success(response.data.message || 'Session time unlinked from subject-teacher successfully');
            return response.data;
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Error unlinking session time from subject-teacher';
            toast.error(errorMessage);
            throw error;
        }
    };

    const getSubjectsWithTeachersByClassId = async (classId) => {
        try {
            const response = await axiosInstance.get(`${endpoints.sessionTimeRanges.getSubjectsTeachersByClass}/${classId}/subjects-teachers`);
            return response.data.data || [];
        } catch (error) {
            toast.error('Error fetching subjects and teachers for class');
            console.error(error.response?.data?.message);
            throw error;
        }
    };

    return {
        sessionTimeRanges,
        totalRecords,
        isLoading,
        fetchSessionTimeRanges,
        createSessionTimeRange,
        getSessionTimeRangeById,
        updateSessionTimeRange,
        updateSessionTimeRangeStatus,
        getAllActiveSessionTimeRanges,
        linkSessionTimeWithSubjectTeacher,
        unlinkSessionTimeWithSubjectTeacher,
        getSubjectsWithTeachersByClassId,
    };
};

export default useSessionTimeRangeService;

